/**
 * Anatomy (speech dictionary + AI prompt file) of the selected template.
 *
 * Single source of truth for "which anatomy / promptFile goes with this
 * template": used by the Patient page's anatomy sync effect AND directly by
 * Process AI, so Process AI never depends on that effect having re-rendered
 * after a template change (the Windows agent selects a template and runs
 * Process AI in the same step — with the lagging state CRE sent the previous
 * template's prompt).
 */

/** Marker: the template list is not loaded yet, so nothing can be decided. */
export const ANATHOMY_UNKNOWN = undefined;

/**
 * @param {{ templateId: *, templates: Array, anathomies: Array }} args
 * @returns {object|null|undefined}
 *   - anatomy object ({ code, name, promptFile, ... }) when the template has one,
 *     `{ code, name: "", promptFile: "" }` when the anatomy list doesn't know the code;
 *   - null when there is no template / the template is not in the list / it has no anatomy;
 *   - undefined (ANATHOMY_UNKNOWN) when the template list is empty (keep whatever is set).
 */
export function resolveTemplateAnathomy({ templateId, templates, anathomies }) {
  // Same checks, in the same order, as the page's original anatomy effect.
  if (!templateId) return null;
  if (!Array.isArray(templates) || templates.length === 0) return ANATHOMY_UNKNOWN;

  const tmpl = templates.find((t) => t && String(t.templateId) === String(templateId));
  if (!tmpl) return null;

  const raw = tmpl.anathomy || "";
  if (!raw) return null;
  const code = String(raw).trim();

  const list = Array.isArray(anathomies) ? anathomies : [];
  const found = list.find((item) => item && item.code === code);
  if (found) return found;
  return { code, name: "", promptFile: "" };
}

export const DEFAULT_PROMPT_FILE = "reporting_ai_main_prompt";

/**
 * Prompt file for Process AI: from the selected template, falling back to the
 * page's anatomy state only while the template list isn't loaded.
 */
export function resolveProcessAiPromptFile({ templateId, templates, anathomies, selectedAnathomy }) {
  const resolved = resolveTemplateAnathomy({ templateId, templates, anathomies });
  const anat = resolved === ANATHOMY_UNKNOWN ? selectedAnathomy : resolved;
  return anat?.promptFile || DEFAULT_PROMPT_FILE;
}

/** Anatomy code the page should show for this template ("" = none), or undefined when unknown. */
export function expectedAnathomyCode({ templateId, templates }) {
  const resolved = resolveTemplateAnathomy({ templateId, templates, anathomies: [] });
  if (resolved === ANATHOMY_UNKNOWN) return ANATHOMY_UNKNOWN;
  return resolved?.code || "";
}
